import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { deleteVulnsSelected, getVulns, hideModalDelete, redirectToManageDetail, setPageNumberVulnAssets, showModalDelete } from 'store/Contextualization/AssetDetail/actions';
import { selectGroupByField, selectPage, selectRowsPerPage } from 'store/Filters/selectors';
import { selectIsAdmin, selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import omit from 'lodash/omit';
import { Wrapper, LeftGroup, RightGroup, Separator, Group, QuickPlannerIcon } from 'Common/Components/CustomActionBar/styled';
import Pagination from 'Common/Components/Pagination';
import CreateActions from './components/CreateActions';
import EditButton from './components/EditButton';
import TagButton from 'Screens/Contextualization/Manage/components/CustomActionBar/components/TagButton';
import CopyLink from 'Screens/Contextualization/Manage/components/CustomActionBar/components/CopyLink';
import CopyApiLink from 'Screens/Contextualization/Manage/components/CustomActionBar/components/CopyApiLink';
import DeleteButton from 'Screens/Contextualization/Manage/components/CustomActionBar/components/DeleteButton';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import RefreshButton from 'Screens/Contextualization/Manage/components/CustomActionBar/components/RefreshButton';
import Severity from 'Screens/Contextualization/Manage/components/CustomActionBar/components/Severity';
import Status from 'Screens/Contextualization/Manage/components/CustomActionBar/components/Status';
import AddNewEvidence from 'Screens/Contextualization/Manage/components/CustomActionBar/components/AddNewEvidence';
import SaveAsTemplate from 'Screens/Contextualization/Manage/components/CustomActionBar/components/SaveAsTemplate';
import ApplyTemplate from 'Screens/Contextualization/Manage/components/CustomActionBar/components/ApplyTemplate';
import AddRemoveColumnsButton from 'Screens/Contextualization/Manage/components/CustomActionBar/components/AddRemoveColumnsButton';
import FilterSideBarIcon from 'Screens/Contextualization/Manage/components/CustomActionBar/components/FilterSidebarIcon';
import DuplicateButton from './components/DuplicateButton';
import { EEFeatureSlot } from 'Common/ee/EEFeatureSlot';
import EELockedFeature from 'Common/Components/EELockedFeature';
import { MODAL_UPGRADE_LICENSE_QUICK_PLANNER } from 'store/modals/modals';
import FFPlanner from 'Images/ff_planner.png';
import { selectCount, selectCurrentAsset, selectIsFetching, selectSelectAllVulns, selectSelected, selectShowDeleteConfirmation, selectShowDetail } from 'store/Contextualization/AssetDetail/selectors';
import { selectVulnsAssetFields } from 'store/Preferences/vulnsAsset/selectors';
import { resetDefault, setFieldVisibility, setNewOrderColumns } from 'store/Preferences/vulnsGeneral/actions';

const ActionBar = ({ entity, showDuplicateModal, setFF }) => {
  const dispatch = useDispatch();
  const vulnsCount = useSelector((state) => selectCount('vulns', state));
  const page = useSelector((state) => selectPage('vulnsAssets', state));
  const rowsPerPage = useSelector((state) => selectRowsPerPage('vulnsAssets', state));
  const hasVulns = vulnsCount > 0;
  const onPrev = () => dispatch(setPageNumberVulnAssets(page - 1));
  const onNext = () => dispatch(setPageNumberVulnAssets(page + 1));
  const isAdmin = useSelector(selectIsAdmin);
  const vulnsSelected = useSelector((state) => selectSelected('vulns', state));
  const isGrouping = useSelector((state) => selectGroupByField('vulnsAssets', state));
  const fields = useSelector(selectVulnsAssetFields);
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  // 'risk'/'cvss' are already blocked from actually rendering in the table (see
  // Columns.jsx), but the Edit Columns picker reads straight from `fields` and doesn't
  // know about the license -- drop them here too so community can't toggle a column that
  // will never show up.
  const editColumnsFields = isAllowedByLicense ? fields : omit(fields, ['risk', 'cvss']);
  const showDeleteConfirmation = useSelector((state) => selectShowDeleteConfirmation('vulns', state));
  const selectAll = useSelector(selectSelectAllVulns);
  const isFetching = useSelector((state) => selectIsFetching('vulns', state));
  const isVulnDetailVisible = useSelector((state) => selectShowDetail('vulns', state));
  const currentAsset = useSelector(selectCurrentAsset);
  const assetIp = currentAsset?.ip;
  const updateFieldVisibility = async (fieldObj, value, type) => {
    await dispatch(setFieldVisibility(fieldObj, value, type));
    if (assetIp) dispatch(getVulns(assetIp));
  };
  const resetColumns = async () => {
    await dispatch(resetDefault());
    if (assetIp) dispatch(getVulns(assetIp));
  };

  return (
    <Wrapper>
      { showDeleteConfirmation
        ? <DeleteConfirmationModal
            showDeleteConfirmation={ showDeleteConfirmation }
            selectAll={ selectAll }
            isFetching={ isFetching }
            vulnsSelected={ vulnsSelected }
            deleteVulns={ () => dispatch(deleteVulnsSelected()) }
            hideModalDelete={ () => dispatch(hideModalDelete('vulns')) }
            vulnsCount={ vulnsCount }
        />
        : null
      }
      <LeftGroup>
        <CreateActions />
        <Separator />
        <Group>
          <EditButton hasVulns={ hasVulns } />
          <TagButton setFF={ setFF } hasItems={ hasVulns } vulnsSelected={ vulnsSelected } tagsEntity="vulnAssetMenu" />
          <CopyLink hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } />
          <CopyApiLink hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } />
          <Severity hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />
          <Status hasVulns={ hasVulns } groupBy={ isGrouping } vulnsSelected={ vulnsSelected } entity={ entity } />
          <AddNewEvidence hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } openEvidenceTab={ (vulnId) => dispatch(redirectToManageDetail(vulnId, 'evidence')) } />
          <SaveAsTemplate hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />
          <ApplyTemplate hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } />
          <DeleteButton hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } showVulnDetail={ isVulnDetailVisible } showModalDelete={ () => dispatch(showModalDelete('vulns')) } />
          <DuplicateButton hasVulns={ hasVulns } showModal={ showDuplicateModal } />
        </Group>
        { isAdmin &&
          <Group>
            <Separator />
            <EEFeatureSlot
              featureKey="quick-planner-button"
              render={ (QuickPlannerButton) => <QuickPlannerButton hasVulns={ hasVulns } entity={ entity } /> }
              fallback={ <EELockedFeature modalId={ MODAL_UPGRADE_LICENSE_QUICK_PLANNER } image={ FFPlanner } descriptionIntlId="upgradeLicense.sidebar.planner.description" title="Quick Planner" icon={ QuickPlannerIcon } /> }
            />
          </Group>
        }
      </LeftGroup>

      <RightGroup>
        { <Pagination offset={ hasVulns ? page : null } limit={ rowsPerPage } total={ vulnsCount } onPrev={ onPrev } onNext={ onNext } />}
        { hasVulns && <Separator /> }
        <Group>
          <RefreshButton entity={ entity } />
          <FilterSideBarIcon entity="vulnsAssets" />
          <AddRemoveColumnsButton
            fields={ editColumnsFields }
            setFieldVisibility={ updateFieldVisibility }
            setNewOrderColumns={ (columns) => dispatch(setNewOrderColumns(columns)) }
            resetDefault={ resetColumns }
          />
        </Group>
      </RightGroup>
    </Wrapper>
  );
};

export default ActionBar;
